import { useState, useRef, useCallback } from "react";
import debounce from "lodash.debounce";
import { C_API } from "@/utils/helper/api";

type Option = { label: string; value: string; iconText?: string; iconUrl?: string };

// Global in-memory cache: key → options array
const optionsCache = new Map<string, Option[]>();

export function useCachedOptions(optionsApiUrl: string, queryParamName: string, perPage: number, cacheKey: string) {
  const [options, setOptions] = useState<Option[]>(() => {
    // Initialize state from cache if present
    return optionsCache.get(cacheKey) ?? [];
  });
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const abortControllerRef = useRef<AbortController | null>(null);

  // Persist options to both state and cache
  const persistOptions = (newOpts: Option[], reset: boolean) => {
    const updated = reset ? newOpts : [...(optionsCache.get(cacheKey) ?? []), ...newOpts];
    optionsCache.set(cacheKey, updated);
    setOptions(updated);
  };

  const fetchPage = async (pageNum: number, query: string, signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const separator = optionsApiUrl.includes("?") ? "&" : "?";
      let url = `${optionsApiUrl}${separator}page=${pageNum}&per_page=${perPage}`;
      if (query) url += `&${queryParamName}=${encodeURIComponent(query)}`;
      const resp = await C_API.get(url, { signal });
      const newOpts = resp.data?.data?.options ?? [];
      persistOptions(newOpts, pageNum === 1);
      setHasMore(newOpts.length === perPage);
    } catch (e) {
      // Don't log abort errors as they are intentional
      if (e instanceof Error && e.name !== "AbortError") {
        console.error("Error fetching options:", e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadOptions = useCallback(
    debounce((query: string, resetPage = false) => {
      // Abort previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      if (resetPage) pageRef.current = 1;
      searchRef.current = query;
      fetchPage(pageRef.current, query, abortControllerRef.current.signal);
    }, 300),
    [optionsApiUrl, queryParamName, perPage, cacheKey]
  );

  const loadNext = () => {
    if (hasMore && !isLoading) {
      // Abort previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      pageRef.current += 1;
      fetchPage(pageRef.current, searchRef.current, abortControllerRef.current.signal);
    }
  };

  const abortRequests = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  return {
    options,
    isLoading,
    hasMore,
    loadOptions,
    loadNext,
    abortRequests,
  };
}
